import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useScreenSize } from 'hooks/useScreenSize';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  selectBillingOrganisation,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { selectUserSelectedOrganisationFeature } from 'store/auth/selectors';
import { useSelector } from 'hooks/useSelector';
import { doSetupBillingAdminClosed, NAVIGATE_MODAL } from 'store/actions';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { theme } from 'lib/theme';
import { DateSelectedMonthsIndicator } from 'components/DateSelectedMonthsIndicator';
import { BillingStatus } from 'types/Billing';
import { pluralise } from 'lib/pluralise';
import { BillingProductCards } from './BillingProductCards';
import { BillingItemCards } from './BillingItemCards';
import { BillingDiscountCards } from './BillingDiscountCards';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsBillingAdmin
>;

export const BillingAdminScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId);
  const hasFeatureSupportBilling = useSelector((state) =>
    selectUserSelectedOrganisationFeature(state, 'billingSupport'),
  );

  const billingOrganisation = useSelector(selectBillingOrganisation);

  React.useEffect(() => {
    return () => {
      dispatch(doSetupBillingAdminClosed());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!organisationId || !billingOrganisation) {
      navigation.goBack();
    }
  }, [navigation, organisationId, billingOrganisation]);

  if (!organisationId || !billingOrganisation) {
    return null;
  }

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          heading="Billing"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <Card
        label="General"
        onLabelLinkPress={() =>
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.SettingsBillingOrganisation,
              params: {
                invoicePrefix: billingOrganisation.invoicePrefix,
                status: billingOrganisation.status,
                invoicePaymentTermsDays:
                  billingOrganisation.invoicePaymentTermsDays,
                assessmentInvoiceMonths:
                  billingOrganisation.assessmentInvoiceMonths,
                hasFreeAssessments: billingOrganisation.hasFreeAssessments,
              },
            }),
          )
        }
        labelLinkText="Change"
        marginBottomQuarter
      >
        <CardRow
          label="Status"
          value={
            billingOrganisation.status === BillingStatus.running
              ? 'Running'
              : 'Paused'
          }
          valueColor={
            billingOrganisation.status === BillingStatus.running
              ? theme.colors.green
              : theme.colors.orange
          }
        />
        <CardRow
          label="Invoice Prefix"
          value={billingOrganisation.invoicePrefix}
        />
        <CardRow
          label="Payment Terms"
          value={pluralise(
            billingOrganisation.invoicePaymentTermsDays,
            '{x} Day',
            '{x} Days',
          )}
        />
      </Card>
      <Card>
        <CardRow label="Assessment Invoice Months">
          <DateSelectedMonthsIndicator
            selectedMonths={billingOrganisation.assessmentInvoiceMonths}
          />
        </CardRow>
        <CardRow
          label="Paid/Free Assessments"
          value={
            billingOrganisation.hasFreeAssessments
              ? 'Free Assessments'
              : 'Paid Assessments'
          }
        />
      </Card>
      {hasFeatureSupportBilling && <BillingProductCards />}
      {hasFeatureSupportBilling && <BillingItemCards />}
      {hasFeatureSupportBilling && <BillingDiscountCards />}
    </PageScrollView>
  );
};
